/**
 * Pure hydration gate for debounced autosave.
 *
 * Problem: watch + debounce often sees a stale snapshot (empty defaults, or
 * pre-reset form) after the server baseline is captured. Comparing that stale
 * debounce to the baseline looks "dirty" and fires a no-op save → Saved flash.
 *
 * Fix: stay disarmed until the *live* snapshot matches the baseline once, then
 * only fire when the *settled* debounced snapshot differs from baseline.
 */

export type AutosaveHydrationState = {
  baseline: string | null;
  armed: boolean;
};

export function createAutosaveHydrationState(
  baseline: string | null = null,
): AutosaveHydrationState {
  return { baseline, armed: false };
}

/** Replace baseline and disarm (server hydrate / identity change). */
export function resetAutosaveBaseline(
  _state: AutosaveHydrationState,
  baseline: string | null,
): AutosaveHydrationState {
  return { baseline, armed: false };
}

/** Commit a successful save as the new baseline; stay armed. */
export function commitAutosaveBaseline(
  state: AutosaveHydrationState,
  baseline: string,
): AutosaveHydrationState {
  return { ...state, baseline, armed: true };
}

/**
 * Observe the live (non-debounced) snapshot. Arms when it matches baseline.
 * Returns the next gate state (may be unchanged).
 */
export function observeAutosaveLiveSnapshot(
  state: AutosaveHydrationState,
  liveSnapshot: string,
): AutosaveHydrationState {
  if (state.baseline == null) return state;
  if (!state.armed && liveSnapshot === state.baseline) {
    return { ...state, armed: true };
  }
  return state;
}

/**
 * Whether a debounced autosave should fire.
 * Requires: armed, debounce settled on live value, and differs from baseline.
 */
export function shouldFireDebouncedAutosave(
  state: AutosaveHydrationState,
  liveSnapshot: string,
  debouncedSnapshot: string,
): boolean {
  if (!state.armed || state.baseline == null) return false;
  if (debouncedSnapshot !== liveSnapshot) return false;
  return debouncedSnapshot !== state.baseline;
}
